import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Smartphone } from "lucide-react";
import PageSeo from "../components/PageSeo";
import Nd1AuthShell from "../nd1/components/Nd1AuthShell";
import Nd1ChangeMobileMarketing from "../nd1/components/Nd1ChangeMobileMarketing";
import "../nd1/nd1.css";
import JobuwinPageLoading from "../components/JobuwinPageLoading";
import TurnstileField from "../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../turnstileConfig";
import {
  isApiConfigured,
  getMobileChangeStatus,
  postMobileChangeStartVerify,
  postMobileChangeResend,
  postMobileChangeVerify,
} from "../apiClient";
import { COUNTRY_DIAL_OPTIONS, getDialCodeForCountryIso } from "../countryDialOptions";

function parseResendCooldownSecondsFromMessage(message) {
  const m = String(message || "").match(/(\d+)\s*seconds/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function ChangeMobilePage({ onSignIn }) {
    const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => {
    const raw = searchParams.get("token");
    return raw ? String(raw).trim() : "";
  }, [searchParams]);

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [status, setStatus] = useState(null);
  const [mobileCountryIso, setMobileCountryIso] = useState("GB");
  const [mobileNational, setMobileNational] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [submittingStart, setSubmittingStart] = useState(false);
  const [submittingVerify, setSubmittingVerify] = useState(false);
  const [resendStatus, setResendStatus] = useState("");
  const [done, setDone] = useState(false);
  const [startTurnstileToken, setStartTurnstileToken] = useState(null);
  const [startTurnstileKey, setStartTurnstileKey] = useState(0);
  const [verifyTurnstileToken, setVerifyTurnstileToken] = useState(null);
  const [verifyTurnstileKey, setVerifyTurnstileKey] = useState(0);
  const [verifyTurnstileSession, setVerifyTurnstileSession] = useState(0);
  const verifyTurnstileSessionRef = useRef(0);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);
  const [smsResendLockedUntilMs, setSmsResendLockedUntilMs] = useState(null);
  const [cooldownNow, setCooldownNow] = useState(() => Date.now());
  const [showContactSupport, setShowContactSupport] = useState(false);

  const displayMaskedPhone =
    status?.valid && status.step === "verify_sms" && status.e164Masked ? String(status.e164Masked) : "";

  useEffect(() => {
    if (!smsResendLockedUntilMs) return undefined;
    const tick = () => {
      const now = Date.now();
      setCooldownNow(now);
      if (now >= smsResendLockedUntilMs) {
        setSmsResendLockedUntilMs(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [smsResendLockedUntilMs]);

  const smsCooldownActive =
    smsResendLockedUntilMs != null && cooldownNow < smsResendLockedUntilMs;
  const smsCooldownSecondsLeft = smsCooldownActive
    ? Math.max(1, Math.ceil((smsResendLockedUntilMs - cooldownNow) / 1000))
    : 0;

  useEffect(() => {
    if (!tokenFromUrl || !isApiConfigured()) {
      setLoadingStatus(false);
      setStatus({ valid: false });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingStatus(true);
      try {
        const s = await getMobileChangeStatus(tokenFromUrl);
        if (!cancelled) setStatus(s);
      } catch {
        if (!cancelled) setStatus({ valid: false });
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl]);

  useEffect(() => {
    if (!status?.valid) return;
    if (status.step === "collect_phone") {
      setShowContactSupport(false);
      return;
    }
    if (status.step !== "verify_sms") return;
    if (status.exhausted) {
      setShowContactSupport(true);
      setSmsResendLockedUntilMs(null);
      return;
    }
    setShowContactSupport(false);
    const until = status.resendLockedUntil;
    if (typeof until === "number" && until > Date.now()) {
      setSmsResendLockedUntilMs(until);
    } else {
      setSmsResendLockedUntilMs(null);
    }
  }, [status]);

  const refreshStatus = async () => {
    if (!tokenFromUrl || !isApiConfigured()) return;
    const s = await getMobileChangeStatus(tokenFromUrl);
    setStatus(s);
  };

  const bumpVerifyTurnstile = () => {
    if (!isTurnstileEnabled() || !isApiConfigured()) return;
    verifyTurnstileSessionRef.current += 1;
    setVerifyTurnstileSession(verifyTurnstileSessionRef.current);
    setVerifyTurnstileKey((k) => k + 1);
    setVerifyTurnstileToken(null);
  };

  const handleVerifyTurnstileChange = (token, widgetSessionId) => {
    if (typeof widgetSessionId === "number" && widgetSessionId !== verifyTurnstileSessionRef.current) {
      return;
    }
    setVerifyTurnstileToken(token);
    if (token) {
      setVerifyError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
    }
  };

  const updateDigit = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setVerifyError("");

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i += 1) {
      next[i] = pasted[i];
    }
    setDigits(next);
    setVerifyError("");
    const focusIndex = Math.min(pasted.length, 5);
    inputsRef.current[focusIndex]?.focus();
  };

  const handleStartVerify = async (event) => {
    event.preventDefault();
    setSubmitError("");
    const dial = getDialCodeForCountryIso(mobileCountryIso);
    const mob = String(mobileNational || "").trim();
    if (!mob) {
      setSubmitError("Enter your new mobile number.");
      return;
    }
    if (!tokenFromUrl) {
      setSubmitError("This page needs a valid link from your email.");
      return;
    }
    if (!isApiConfigured()) {
      setSubmitError("The app is not connected to the API.");
      return;
    }

    setSubmittingStart(true);
    try {
      if (isTurnstileEnabled() && !startTurnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && startTurnstileToken ? String(startTurnstileToken) : undefined;
      await postMobileChangeStartVerify({
        token: tokenFromUrl,
        countryCode: dial,
        mobile: mob,
        turnstileToken: turnstilePayload,
      });
      setStartTurnstileKey((k) => k + 1);
      setStartTurnstileToken(null);
      setResendStatus("");
      await refreshStatus();
    } catch (err) {
      const c = err && typeof err === "object" && "code" in err ? err.code : undefined;
      if (c === "PHONE_SMS_SENDS_EXHAUSTED") {
        setShowContactSupport(true);
        setSubmitError("");
      } else {
        const raw = err instanceof Error ? err.message : "Something went wrong";
        setSubmitError(formatTurnstileErrorForUser(raw));
      }
      setStartTurnstileKey((k) => k + 1);
      setStartTurnstileToken(null);
    } finally {
      setSubmittingStart(false);
    }
  };

  const handleVerifySubmit = async (event) => {
    event.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) {
      setVerifyError("Enter the full 6-digit code.");
      return;
    }
    if (!tokenFromUrl) {
      setVerifyError("This page needs a valid link from your email.");
      return;
    }

    setVerifyError("");
    setResendStatus("");
    setSubmittingVerify(true);
    try {
      if (isTurnstileEnabled() && !verifyTurnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && verifyTurnstileToken ? String(verifyTurnstileToken) : undefined;
      await postMobileChangeVerify({
        token: tokenFromUrl,
        code,
        turnstileToken: turnstilePayload,
      });
      if (typeof onSignIn === "function") {
        onSignIn(null, { token: null });
      }
      setDone(true);
      bumpVerifyTurnstile();
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Verification failed";
      setVerifyError(formatTurnstileErrorForUser(raw));
      bumpVerifyTurnstile();
    } finally {
      setSubmittingVerify(false);
    }
  };

  const handleResend = async () => {
    setVerifyError("");
    setResendStatus("");
    setSubmittingVerify(true);
    try {
      if (isTurnstileEnabled() && !verifyTurnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && verifyTurnstileToken ? String(verifyTurnstileToken) : undefined;
      await postMobileChangeResend({
        token: tokenFromUrl,
        turnstileToken: turnstilePayload,
      });
      setResendStatus("We sent another code by SMS.");
      bumpVerifyTurnstile();
      const st = await getMobileChangeStatus(tokenFromUrl);
      setStatus(st);
      if (st?.exhausted) {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
      } else {
        const until = st?.resendLockedUntil;
        if (typeof until === "number" && until > Date.now()) {
          setSmsResendLockedUntilMs(until);
        } else {
          setSmsResendLockedUntilMs(null);
        }
      }
    } catch (err) {
      const c = err && typeof err === "object" && "code" in err ? err.code : undefined;
      if (c === "PHONE_SMS_SENDS_EXHAUSTED") {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
        setVerifyError("");
      } else if (c === "RESEND_COOLDOWN") {
        const sec = parseResendCooldownSecondsFromMessage(err instanceof Error ? err.message : "");
        if (sec != null) {
          setSmsResendLockedUntilMs(Date.now() + sec * 1000);
        }
        setVerifyError("");
      } else {
        const raw = err instanceof Error ? err.message : "Could not resend";
        setVerifyError(formatTurnstileErrorForUser(raw));
        bumpVerifyTurnstile();
      }
    } finally {
      setSubmittingVerify(false);
    }
  };

  const showCollect =
    !loadingStatus && status?.valid && status.step === "collect_phone" && !done && !showContactSupport;
  const showVerify =
    !loadingStatus && status?.valid && status.step === "verify_sms" && !done && !showContactSupport;
  const showInvalid =
    !loadingStatus && (!status || !status.valid) && !done && tokenFromUrl && isApiConfigured();
  const showMissingToken = !tokenFromUrl && !done;

  const showTurnstileStart = isTurnstileEnabled() && isApiConfigured() && showCollect;
  const showTurnstileVerify = isTurnstileEnabled() && isApiConfigured() && showVerify;

  const formCard = (
    <section className="nd1-auth-card nd1-auth-card--signin">
          <Link className="nd1-auth-back" to="/signin">
            <ArrowLeft className="nd1-icon" aria-hidden="true" />
            Back to sign in
          </Link>
          <p className="nd1-auth-card__eyebrow">Change mobile</p>
          {loadingStatus ? (
            <JobuwinPageLoading
              compact
              title="Loading…"
              subtitle="Validating your secure link."
            />
          ) : (
            <h2>
              {done
                ? "Mobile number updated"
                : showVerify
                  ? "Confirm your new mobile"
                  : "Enter your new mobile number"}
            </h2>
          )}

          {showMissingToken && !loadingStatus ? (
            <div role="alert">
              <p className="nd1-auth-card__lede">
                This page needs a valid link from your email. Request a change from your profile after signing in.
              </p>
              <p className="verification-help" >
                <Link className="nd1-auth-link-inline" to="/applicant-profile">
                  Go to profile
                </Link>
              </p>
            </div>
          ) : null}

          {!isApiConfigured() && !done ? (
            <p className="nd1-auth-card__lede" role="alert">
              The app is not connected to the API. Mobile change is unavailable in this mode.
            </p>
          ) : null}

          {showInvalid ? (
            <div role="alert">
              <p className="nd1-auth-card__lede">
                This link is invalid or has expired. Request a new mobile change from your JOBUWIN profile.
              </p>
              <p className="verification-help" >
                <Link className="nd1-auth-link-inline" to="/applicant-profile">
                  Go to profile
                </Link>
              </p>
            </div>
          ) : null}

          {done ? (
            <>
              <p className="nd1-auth-card__lede">
                Your mobile number was updated. For security, you have been signed out on all devices. Please sign in
                again.
              </p>
              <p className="verification-help" >
                <Link className="nd1-auth-link-inline" to="/signin">
                  Go to sign in
                </Link>
              </p>
            </>
          ) : null}

          {showContactSupport ? (
            <div className="nd1-help-contact-panel" role="alert">
              <p className="nd1-help-contact-panel__text">
                Please email{" "}
                <a className="nd1-help-contact-panel__link" href="mailto:help@jobuwin.com">
                  help@jobuwin.com
                </a>{" "}
                and we&apos;ll help you with the next steps.
              </p>
              <p className="nd1-help-contact-panel__footer">
                <Link className="nd1-auth-link-inline" to="/applicant-profile">
                  Back to profile
                </Link>
              </p>
            </div>
          ) : null}

          {showCollect ? (
            <>
              <p className="nd1-auth-card__lede">
                Enter the mobile number you want on your account. After you tap Send verification SMS, you cannot
                switch to a different number on this page; you would need a new change link from your profile. We send
                a code by SMS (same flow as when you signed up).
              </p>
              {!status?.smsConfigured ? (
                <p className="nd1-auth-card__lede" role="alert">
                  SMS verification is not configured on the server. You cannot complete a mobile change until Twilio
                  Verify is set up.
                </p>
              ) : null}

              <form id="jobuwin-change-mobile-form" className="nd1-auth-form" onSubmit={handleStartVerify}>
                <label htmlFor="changeMobileCountry">Country</label>
                <select
                  id="changeMobileCountry"
                  name="changeMobileCountry"
                  value={mobileCountryIso}
                  onChange={(e) => setMobileCountryIso(e.target.value)}
                  aria-label="Country calling code"
                >
                  {COUNTRY_DIAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <label htmlFor="changeMobileNational">Mobile number</label>
                <input
                  id="changeMobileNational"
                  name="changeMobileNational"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="7123 456789"
                  value={mobileNational}
                  onChange={(e) => setMobileNational(e.target.value)}
                  required
                />

                {!(isTurnstileEnabled() && isApiConfigured()) && submitError ? (
                  <p className="nd1-auth-alert" role="alert">
                    {submitError}
                  </p>
                ) : null}

                {!(isTurnstileEnabled() && isApiConfigured()) ? (
                  <button type="submit" className="nd1-btn nd1-btn--primary nd1-auth-submit" disabled={submittingStart || !status?.smsConfigured}>
                    {submittingStart ? "Sending..." : "Send verification SMS"}
                    <ArrowRight className="nd1-icon" aria-hidden="true" />
                  </button>
                ) : null}
              </form>

              {showTurnstileStart ? (
                <div className="nd1-signup-captcha">
                  {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="nd1-auth-alert" role="alert">
                      {submitError}
                    </p>
                  ) : null}
                  <TurnstileField
                    tokenSessionId={0}
                    onTokenChange={(token) => {
                      setStartTurnstileToken(token);
                      if (token) {
                        setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
                      }
                    }}
                    widgetKey={`change-mobile-start-${startTurnstileKey}`}
                    options={{ action: "change-mobile-start" }}
                  />
                  <p className="nd1-turnstile-note">
                    Security check: making sure you are a genuine human :-)
                  </p>
                  {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="nd1-auth-alert" role="alert">
                      {submitError}
                    </p>
                  ) : null}
                  <button
                    type="submit"
                    form="jobuwin-change-mobile-form"
                    className="nd1-btn nd1-btn--primary nd1-auth-submit"
                    disabled={submittingStart || !status?.smsConfigured}
                  >
                    {submittingStart ? "Sending..." : "Send verification SMS"}
                    <ArrowRight className="nd1-icon" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {showVerify ? (
            <>
              <p className="nd1-auth-card__lede">
                Enter the 6-digit code we sent by SMS to <strong>{displayMaskedPhone}</strong>.
              </p>
              <p className="nd1-auth-card__lede" >
                To use a different number, complete this step or request a new mobile change link from your profile
                (this avoids extra SMS sends to new numbers).
              </p>

              <form className="nd1-auth-form nd1-auth-code-form" id="jobuwin-change-mobile-verify-form" onSubmit={handleVerifySubmit}>
                <label htmlFor="change-mobile-code-0">Verification code</label>
                <div className="code-inputs" onPaste={handlePaste}>
                  {digits.map((digit, index) => (
                    <input
                      key={`cm-digit-${index}`}
                      id={`change-mobile-code-${index}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={digit}
                      onChange={(event) => updateDigit(index, event.target.value)}
                      onKeyDown={(event) => handleKeyDown(index, event)}
                      ref={(element) => {
                        inputsRef.current[index] = element;
                      }}
                    />
                  ))}
                </div>

                {resendStatus ? <p className="verification-help">{resendStatus}</p> : null}

                {!(isTurnstileEnabled() && isApiConfigured()) && verifyError ? (
                  <p className="nd1-auth-alert" role="alert">
                    {verifyError}
                  </p>
                ) : null}

                {!(isTurnstileEnabled() && isApiConfigured()) ? (
                  <button type="submit" className="nd1-btn nd1-btn--primary nd1-auth-submit" disabled={submittingVerify || digits.join("").length !== 6}>
                    {submittingVerify ? "Verifying..." : "Confirm new mobile"}
                    <ArrowRight className="nd1-icon" aria-hidden="true" />
                  </button>
                ) : null}
              </form>

              {showTurnstileVerify ? (
                <div className="nd1-signup-captcha">
                  {verifyError === TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="nd1-auth-alert" role="alert">
                      {verifyError}
                    </p>
                  ) : null}
                  <TurnstileField
                    tokenSessionId={verifyTurnstileSession}
                    onTokenChange={handleVerifyTurnstileChange}
                    widgetKey={`change-mobile-verify-${verifyTurnstileKey}`}
                    options={{ action: "change-mobile-verify" }}
                  />
                  <p className="nd1-turnstile-note">
                    Security check: making sure you are a genuine human :-)
                  </p>
                  {verifyError && verifyError !== TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="nd1-auth-alert" role="alert">
                      {verifyError}
                    </p>
                  ) : null}
                  <button
                    type="submit"
                    form="jobuwin-change-mobile-verify-form"
                    className="nd1-btn nd1-btn--primary nd1-auth-submit"
                    disabled={submittingVerify || digits.join("").length !== 6}
                  >
                    {submittingVerify ? "Verifying..." : "Confirm new mobile"}
                    <ArrowRight className="nd1-icon" aria-hidden="true" />
                  </button>
                </div>
              ) : null}

              <div className="nd1-verify-mobile-send nd1-verify-mobile-send--after">
                <button
                  type="button"
                  className="nd1-btn nd1-btn--accent"
                  onClick={handleResend}
                  disabled={
                    submittingVerify ||
                    smsCooldownActive ||
                    (showTurnstileVerify && !verifyTurnstileToken)
                  }
                >
                  {submittingVerify
                    ? "Please wait…"
                    : smsCooldownActive
                      ? `Wait ${smsCooldownSecondsLeft}s`
                      : "Send SMS again"}
                </button>
              </div>
            </>
          ) : null}
        </section>
  );

  return (
    <Nd1AuthShell hideFooter>
      <PageSeo
        title="Change mobile number"
        description="Update the mobile number linked to your JOBUWIN account."
        path="/change-mobile"
        canonicalPath="/change-mobile"
        noindex
      />
      <main id="nd1-main" className="nd1-shell nd1-auth-shell nd1-auth-shell--signin">
        <div className="nd1-auth-panel">
          <Nd1ChangeMobileMarketing />
          {formCard}
        </div>
      </main>
    </Nd1AuthShell>
  );
}

export default ChangeMobilePage;
